import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Sparkles, Shield, Home, HelpCircle } from "lucide-react";
import ScrollReveal from "./ScrollReveal";

const tips = [
  {
    category: "care",
    question: "How do I clean and maintain hardwood floors?",
    answer:
      "Sweep or vacuum regularly to remove dust and debris. Use a damp (not wet) mop with a pH-neutral hardwood cleaner. Avoid excessive water, steam mops, and harsh chemicals. Place felt pads under furniture legs and use mats at entryways to prevent scratches.",
  },
  {
    category: "care",
    question: "How should I care for LVP or laminate flooring?",
    answer:
      "LVP and laminate are low-maintenance. Sweep or vacuum weekly and damp mop as needed. Avoid abrasive cleaners or scrub brushes. Wipe up spills promptly—while LVP is waterproof, standing water can seep into seams over time.",
  },
  {
    category: "care",
    question: "Any special tips for Colorado's dry climate?",
    answer:
      "Colorado's low humidity can cause hardwood to shrink and gap. Run a humidifier during winter months to maintain 35-55% indoor humidity. This protects your floors and keeps them looking their best year-round.",
  },
  {
    category: "choosing",
    question: "What's the difference between hardwood, LVP, and laminate?",
    answer:
      "Hardwood is real wood that can be sanded and refinished multiple times—it's timeless and adds home value. LVP (Luxury Vinyl Plank) is 100% waterproof and scratch-resistant, great for basements and busy households. Laminate is budget-friendly with a realistic wood look but can't be refinished.",
  },
  {
    category: "choosing",
    question: "Which flooring is best for homes with pets or kids?",
    answer:
      "LVP is the top choice for active households—it's scratch-resistant, waterproof, and easy to clean. Hardwood with a durable finish (like aluminum oxide) also works well but may show scratches over time. Both are excellent options depending on your style preference and budget.",
  },
  {
    category: "choosing",
    question: "Can I install hardwood in my basement?",
    answer:
      "Traditional hardwood isn't recommended for basements due to moisture concerns. LVP is the ideal choice—it's 100% waterproof and handles temperature fluctuations well. We can help you find an LVP that looks just like real hardwood.",
  },
  {
    category: "project",
    question: "How should I prepare my home for floor installation?",
    answer:
      "Clear the rooms of furniture, décor, and fragile items. We'll handle moving heavy pieces if needed. Ensure good ventilation and plan for some dust during sanding projects. We always protect adjacent areas with plastic sheeting.",
  },
  {
    category: "project",
    question: "How long does a typical flooring project take?",
    answer:
      "New installations typically take 1-3 days depending on the area size. Sand and refinish projects usually run 3-5 days, including dry time between coats. We'll give you a specific timeline during your in-home estimate.",
  },
];

const categoryIcons: Record<string, React.ReactNode> = {
  care: <Sparkles className="h-5 w-5 text-primary" />,
  choosing: <Home className="h-5 w-5 text-primary" />,
  project: <Shield className="h-5 w-5 text-primary" />,
};

const FlooringTipsSection = () => {
  return (
    <section className="py-16 md:py-24 bg-secondary/30">
      <div className="container mx-auto px-4">
        <ScrollReveal>
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 mb-4">
              <HelpCircle className="h-6 w-6 text-primary" />
              <span className="text-primary font-semibold uppercase tracking-wider text-sm">
                Helpful Resources
              </span>
            </div>
            <h2 className="section-heading">Flooring Tips & Care Guide</h2>
            <p className="section-subheading max-w-2xl mx-auto">
              Everything you need to know about choosing, maintaining, and
              preparing for your new floors.
            </p>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={0.1}>
          <div className="max-w-3xl mx-auto bg-card rounded-lg shadow-lg p-6 md:p-8">
            <Accordion type="single" collapsible className="w-full">
              {tips.map((tip, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left hover:no-underline group">
                    <div className="flex items-center gap-3">
                      <span className="flex-shrink-0 opacity-70 group-hover:opacity-100 transition-opacity">
                        {categoryIcons[tip.category]}
                      </span>
                      <span className="font-heading font-semibold text-foreground group-hover:text-primary transition-colors">
                        {tip.question}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pl-8">
                    {tip.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={0.2}>
          <p className="text-center text-muted-foreground mt-8 text-sm">
            Have more questions? We're happy to help —{" "}
            <a
              href="tel:720-600-1156"
              className="text-primary font-semibold hover:underline"
            >
              give us a call
            </a>{" "}
            or{" "}
            <a
              href="/contact"
              className="text-primary font-semibold hover:underline"
            >
              send us a message
            </a>
            .
          </p>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default FlooringTipsSection;
